import { ref } from 'vue';

import type { Filters } from '@/types/mode-list-filters';

import useLocale from './useLocale';
import useModeListData from './useModeListData';

const { currentLocale } = useLocale();
const filters = ref<Filters>({
  size: 16,
  filters: [
    {
      filterProperties: {
        type: 'publication_states',
        target: 'product',
      },
      selection: ['PUBLISHED'],
    },
    {
      filterProperties: {
        type: 'distributor',
      },
    },
    // {
    //   filterProperties: {
    //     type: 'available_product',
    //   },
    // },
  ],
  sorts: [{ type: 'random', seed: Date.now() }] as Array<any>,
  selections: [],
  metrics: [
    { type: 'priceFromWithSelection' },
    { type: 'sessionDurationWithSelection' },
    { type: 'taxonomyThemes' },
    { type: 'taxonomyAudiences' },
    { type: 'taxonomyInternals' },
    { type: 'taxonomyPlaces' },
    { type: 'taxonomyPlacesWithSelection' },
    { type: 'taxonomyTypes' },
    {
      type: 'timeRangeWithSelection',
      ranges: [
        { minTime: 480, maxTime: 720 },
        { minTime: 720, maxTime: 1020 },
        { minTime: 1020, maxTime: 1440 },
      ],
    },
    {
      type: 'crossedTaxonomyCount',
      taxonomyTypes: ['TYPES', 'THEMES', 'PLACES'],
      crossedTaxonomies: {
        themes: [],
        places: [],
        types: [],
      },
    },
  ] as Array<any>,
  properties: [{ type: 'nextSessionDate' }, { type: 'rating' }, { type: 'product_enrichment_by_distributor' }] as Array<any>,
});
const datesAsStrings = ref<string | null>(null);

export default function useModeListFilters() {
  const STORAGE_KEY = 'yb-filters';

  function saveFiltersToLocalStorage() {
    localStorage.setItem(STORAGE_KEY, JSON.stringify({
      data: filters.value,
      tts: Date.now(),
    }));
  }

  function loadFiltersFromLocalStorage() {
    const savedFilters = localStorage.getItem(STORAGE_KEY);
    if (!savedFilters)
      return;
    const parsedFilters = JSON.parse(savedFilters);
    const validTTS = (Date.now() - parsedFilters.tts) < (30 * 60 * 1000);
    if (validTTS) {
      filters.value = parsedFilters.data;
    }
    else {
      localStorage.removeItem(STORAGE_KEY);
    }
  }

  function addSearchingFilter(newSearchFilter: string) {
    const existingIndex = filters.value.sorts.findIndex(it => it.type === 'search');
    if (!newSearchFilter.length) {
      if (existingIndex > -1)
        filters.value.sorts.splice(existingIndex, 1);
    }
    else {
      if (existingIndex > -1) {
        filters.value.sorts[existingIndex].query = newSearchFilter;
      }
      else {
        filters.value.sorts.push({ type: 'search', query: newSearchFilter });
      }
    }
    filters.value.selections = filters.value.selections.filter((it: any) => !['taxonomyThemes', 'taxonomyTypes'].includes(it.filterProperties?.type));
    filters.value.metrics = filters.value.metrics.filter((it: any) => it.type !== 'crossedTaxonomyCount');
    saveFiltersToLocalStorage();
  }

  function addDatesFilter(dates: { start: number; end: number }) {
    setDatesAsStrings(dates);
    const existingDatesFiltersIndex = filters.value.filters?.findIndex(it => it.filterProperties.type === 'date_range');
    if (existingDatesFiltersIndex! > -1) {
      filters.value.filters?.splice(existingDatesFiltersIndex!, 1);
    }
    filters.value.filters?.push({
      filterProperties: { type: 'date_range' },
      selection: { endDate: dates.end, startDate: dates.start },
    });
    saveFiltersToLocalStorage();
    const { fetchProductsList } = useModeListData();
    fetchProductsList(filters.value);
  }

  function setDatesAsStrings(dates: { start: number; end: number }) {
    const startDate = new Intl.DateTimeFormat(currentLocale.value, { day: 'numeric', month: 'short' }).format(dates.start);
    const endDate = new Intl.DateTimeFormat(currentLocale.value, { day: 'numeric', month: 'short' }).format(dates.end);
    datesAsStrings.value = dates.start === dates.end ? startDate : `${startDate} - ${endDate}`;
  }

  function addSortingFilter(newSorting: string) {
    filters.value.sorts = [];
    if (newSorting === 'relevance') {
      saveFiltersToLocalStorage();
      const { fetchProductsList } = useModeListData();
      fetchProductsList(filters.value);
      return;
    }
    filters.value.sorts.push({ type: newSorting });
    saveFiltersToLocalStorage();
    const { fetchProductsList } = useModeListData();
    fetchProductsList(filters.value);
  }

  function addPlacesFilters(newPlaces: any[]) {
    const { fetchProductsList } = useModeListData();
    const taxonomyPlacesSelection = filters.value.selections.findIndex(it => it.filterProperties?.type === 'taxonomyPlaces');

    if (newPlaces.length === 0) {
      if (taxonomyPlacesSelection > -1) {
        filters.value.selections.splice(taxonomyPlacesSelection, 1);
      }
      saveFiltersToLocalStorage();
      fetchProductsList(filters.value);
      return;
    }

    if (taxonomyPlacesSelection > -1) {
      filters.value.selections[taxonomyPlacesSelection].selection = [];
      for (const place of newPlaces) {
        filters.value.selections[taxonomyPlacesSelection].selection.push(place.id);
      }
    }
    else {
      filters.value.selections.push({
        filterProperties: { type: 'taxonomyPlaces' },
        selection: newPlaces.map((it: any) => it.id),
      });
    }
    saveFiltersToLocalStorage();
    fetchProductsList(filters.value);
  }

  return {
    filters,
    datesAsStrings,
    setDatesAsStrings,
    addSearchingFilter,
    addDatesFilter,
    addSortingFilter,
    addPlacesFilters,
    loadFiltersFromLocalStorage,
    saveFiltersToLocalStorage,
  };
}
